<template>
  <div class="cue-card" :class="{ 'cue-card--expanded': isExpanded }">
    <button type="button" class="cue-card__header" @click="emit('toggle')">
      <div class="cue-card__header-left">
        <Icon :icon="config.icon" class="cue-card__icon" />
        <span class="cue-card__title">{{ config.title }}</span>
        <span class="cue-card__badge" :class="`cue-card__badge--${config.badgeVariant}`">
          {{ dynamicBadgeLabel }}
        </span>
      </div>
      <Icon
        icon="mdi:chevron-down"
        class="cue-card__chevron"
        :class="{ 'cue-card__chevron--open': isExpanded }"
      />
    </button>

    <div v-if="isExpanded" class="cue-card__body">
      <!-- Call / Voicemail Script: 4-step layout -->
      <template v-if="stepsData">
        <div class="cue-card__steps">
          <div
            v-for="(step, idx) in stepsData.steps"
            :key="idx"
            class="cue-card__step"
            :style="{ borderLeftColor: step.color }"
          >
            <span class="cue-card__step-label" :style="{ color: step.color }">
              {{ step.label }}
            </span>
            <p class="cue-card__step-content" v-html="step.content"></p>
          </div>
        </div>
      </template>

      <!-- Email: subject + body -->
      <template v-else-if="config.channel === 'email' && data.email">
        <div class="cue-card__email">
          <div class="cue-card__email-subject">
            <span class="cue-card__email-subject-label">Subject:</span>
            <span class="cue-card__email-subject-value">{{ data.email.subject }}</span>
          </div>
          <div class="cue-card__text" v-html="data.email.body"></div>
        </div>
      </template>

      <!-- LinkedIn InMail: subject + body -->
      <template v-else-if="config.channel === 'linkedin_inmail' && data.linkedin_inmail">
        <div class="cue-card__email">
          <div class="cue-card__email-subject">
            <span class="cue-card__email-subject-label">Subject:</span>
            <span class="cue-card__email-subject-value">{{ data.linkedin_inmail.subject }}</span>
          </div>
          <p class="cue-card__text" v-html="data.linkedin_inmail.body"></p>
        </div>
      </template>

      <!-- Generic: body only -->
      <template v-else>
        <p class="cue-card__text" v-html="bodyContent"></p>
      </template>

      <div class="cue-card__actions">
        <button
          type="button"
          class="cue-card__action-btn cue-card__action-btn--copy"
          :title="copyLabel"
          @click="handleCopy"
        >
          <Icon :icon="copyLabel === 'Copied!' ? 'mdi:check' : 'mdi:content-copy'" />
        </button>
        <button
          type="button"
          class="cue-card__action-btn cue-card__action-btn--use"
          @click="handleUse"
        >
          <span>Use in {{ config.shortTitle }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { Icon } from "@iconify/vue";
import type { CueCardConfig, OutreachCueCardsData } from "@/types/outreach-cue-cards";

interface Props {
  config: CueCardConfig;
  data: OutreachCueCardsData;
  isExpanded: boolean;
}

const props = defineProps<Props>();

function htmlToText(html: string): string {
  return html.replace(/<br\s*\/?>/gi, "\n").replace(/<[^>]*>/g, "");
}

const emit = defineEmits<{
  toggle: [];
  copy: [channel: string];
  "use-content": [
    payload: {
      channel: string;
      content: string;
      subject?: string;
      callSteps?: { label: string; content: string; color: string }[];
    },
  ];
}>();

const copyLabel = ref("Copy");
let copyTimeout: ReturnType<typeof setTimeout> | null = null;

const stepsData = computed(() => {
  const ch = props.config.channel;
  if (ch === "call" && props.data.call) return props.data.call;
  if (ch === "voicemail" && props.data.voicemail) return props.data.voicemail;
  return null;
});

const bodyContent = computed(() => {
  const ch = props.config.channel;
  if (ch === "call" || ch === "voicemail" || ch === "email") return "";
  const channelData = props.data[ch] as { body: string };
  return channelData?.body || "";
});

const dynamicBadgeLabel = computed(() => {
  const ch = props.config.channel;
  if ((ch === "call" || ch === "voicemail") && stepsData.value) {
    const count = stepsData.value.steps.length;
    return `${count} Steps`;
  }
  if (ch === "email" && props.data.email) {
    const subject = props.data.email.subject || "";
    return subject.length > 40 ? subject.slice(0, 40) + "..." : subject;
  }
  if (ch === "linkedin_inmail" && props.data.linkedin_inmail) {
    const subject = props.data.linkedin_inmail.subject || "";
    return subject.length > 40 ? subject.slice(0, 40) + "..." : subject;
  }
  // For other channels, show character count
  const channelData = props.data[ch] as { body: string } | undefined;
  const body = channelData?.body || "";
  return `${htmlToText(body).length} chars`;
});

const getFullContent = (): string => {
  const ch = props.config.channel;
  if ((ch === "call" || ch === "voicemail") && stepsData.value) {
    return stepsData.value.steps.map((s) => `[${s.label}]\n${htmlToText(s.content)}`).join("\n\n");
  }
  if (ch === "email" && props.data.email) {
    return `Subject: ${props.data.email.subject}\n\n${htmlToText(props.data.email.body)}`;
  }
  if (ch === "linkedin_inmail" && props.data.linkedin_inmail) {
    return `Subject: ${props.data.linkedin_inmail.subject}\n\n${htmlToText(props.data.linkedin_inmail.body)}`;
  }
  const channelData = props.data[ch] as { body: string };
  return htmlToText(channelData?.body || "");
};

const handleCopy = async () => {
  try {
    await navigator.clipboard.writeText(getFullContent());
    copyLabel.value = "Copied!";
    if (copyTimeout) clearTimeout(copyTimeout);
    copyTimeout = setTimeout(() => {
      copyLabel.value = "Copy";
    }, 2000);
    emit("copy", props.config.channel);
  } catch {
    copyLabel.value = "Failed";
    if (copyTimeout) clearTimeout(copyTimeout);
    copyTimeout = setTimeout(() => {
      copyLabel.value = "Copy";
    }, 2000);
  }
};

const handleUse = () => {
  const ch = props.config.channel;
  if (ch === "linkedin_inmail") {
    emit("use-content", {
      channel: ch,
      content: props.data.linkedin_inmail?.body || "",
      subject: props.data.linkedin_inmail?.subject,
    });
    return;
  }
  const content = ch === "email" ? props.data.email?.body || "" : getFullContent();
  const subject = ch === "email" ? props.data.email?.subject : undefined;
  const callSteps = stepsData.value ? stepsData.value.steps : undefined;
  emit("use-content", { channel: ch, content, subject, callSteps });
};
</script>

<style scoped>
.cue-card {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  transition: box-shadow 0.2s;
}

.cue-card--expanded {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.cue-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 14px;
  background: #fafafa;
  border: none;
  cursor: pointer;
  transition: background 0.15s;
}

.cue-card__header:hover {
  background: #f3f4f6;
}

.cue-card__header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
}

.cue-card__icon {
  font-size: 1.125rem;
  color: #6b7280;
}

.cue-card__title {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #1f2937;
  flex-shrink: 0;
}

.cue-card__badge {
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  font-size: 0.6875rem;
  font-weight: 600;
  border-radius: 10px;
  line-height: 1.5;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px;
}

.cue-card__badge--call {
  background: #dbeafe;
  color: #1d4ed8;
}

.cue-card__badge--email {
  background: #fce7f3;
  color: #be185d;
}

.cue-card__badge--linkedin {
  background: #e0e7ff;
  color: #4338ca;
}

.cue-card__badge--whatsapp {
  background: #d1fae5;
  color: #065f46;
}

.cue-card__badge--sms {
  background: #fef3c7;
  color: #92400e;
}

.cue-card__chevron {
  font-size: 1.125rem;
  color: #9ca3af;
  transition: transform 0.2s;
  flex-shrink: 0;
}

.cue-card__chevron--open {
  transform: rotate(180deg);
}

.cue-card__body {
  padding: 12px 14px;
  border-top: 1px solid #e5e7eb;
  background: #ffffff;
}

/* Call script steps */
.cue-card__steps {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.cue-card__step {
  padding: 8px 12px;
  border-left: 3px solid #d1d5db;
  background: #f9fafb;
  border-radius: 0 6px 6px 0;
}

.cue-card__step-label {
  display: block;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
}

.cue-card__step-content {
  font-size: 0.8125rem;
  color: #374151;
  line-height: 1.5;
  margin: 0;
}

/* Email */
.cue-card__email {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cue-card__email-subject {
  display: flex;
  align-items: baseline;
  gap: 6px;
  padding: 6px 10px;
  background: #f9fafb;
  border-radius: 6px;
}

.cue-card__email-subject-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  flex-shrink: 0;
}

.cue-card__email-subject-value {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #1f2937;
}

/* Generic text */
.cue-card__text {
  font-size: 0.8125rem;
  color: #374151;
  line-height: 1.6;
  margin: 0;
  white-space: pre-wrap;
}

/* Actions */
.cue-card__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid #f3f4f6;
}

.cue-card__action-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.15s;
}

.cue-card__action-btn--copy {
  background: transparent;
  color: #6b7280;
  padding: 6px;
  border-radius: 6px;
}

.cue-card__action-btn--copy:hover {
  color: #374151;
  background: #f3f4f6;
}

.cue-card__action-btn--use {
  background: #ffffff;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 20px;
  padding: 5px 14px;
}

.cue-card__action-btn--use:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}
</style>
